// src/pages/AdminCashReport.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  limit,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";

function fmtAmount(n) {
  const v = Number(n || 0);
  return v.toLocaleString() + " XOF";
}

function fmtDate(ts) {
  if (!ts) return "—";
  try {
    const d = ts?.toDate?.() || new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

export default function AdminCashReport() {
  // Filtres
  const [locationId, setLocationId] = useState(""); // ex: "boutique"
  const [from, setFrom] = useState(""); // "2025-11-01"
  const [to, setTo] = useState("");
  const [cashierFilter, setCashierFilter] = useState("");

  // Données sessions
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // Profils utilisateurs (uid -> {displayName,email})
  const [userMap, setUserMap] = useState({});

  // Détails d'une session (liste des ventes)
  const [detailSession, setDetailSession] = useState(null);
  const [detailSales, setDetailSales] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // ================== User profiles ==================

  async function hydrateUsersFromSessions(sessionsArr) {
    const uids = new Set();

    sessionsArr.forEach((s) => {
      if (s.openedBy) uids.add(String(s.openedBy));
      if (s.closedBy) uids.add(String(s.closedBy));
    });

    if (!uids.size) {
      setUserMap({});
      return;
    }

    const entries = {};
    await Promise.all(
      Array.from(uids).map(async (uid) => {
        try {
          const snap = await getDoc(doc(db, "users", uid));
          if (snap.exists()) {
            const data = snap.data() || {};
            entries[uid] = {
              displayName: data.displayName || data.name || "",
              email: data.email || "",
            };
          }
        } catch (e) {
          console.error("Erreur chargement user", uid, e);
        }
      })
    );

    setUserMap(entries);
  }

  function displayUser(uid) {
    if (!uid) return "—";
    const u = userMap[uid];
    if (!u) return uid;
    if (u.displayName && u.email) {
      return `${u.displayName} (${u.email})`;
    }
    return u.displayName || u.email || uid;
  }

  // ================== Chargement sessions ==================

  async function loadSessions() {
    setLoading(true);
    setMsg("");
    setSessions([]);
    setDetailSession(null);
    setDetailSales([]);

    try {
      const constraints = [];

      if (locationId) {
        constraints.push(where("locationId", "==", locationId));
      }

      // filtres de date sur openedAt
      if (from) {
        const dFrom = new Date(from + "T00:00:00");
        constraints.push(where("openedAt", ">=", dFrom));
      }
      if (to) {
        const dTo = new Date(to + "T23:59:59");
        constraints.push(where("openedAt", "<=", dTo));
      }

      let qy;
      if (constraints.length) {
        qy = query(
          collection(db, "cash_sessions"),
          ...constraints,
          orderBy("openedAt", "desc"),
          limit(200)
        );
      } else {
        qy = query(
          collection(db, "cash_sessions"),
          orderBy("openedAt", "desc"),
          limit(200)
        );
      }

      const snap = await getDocs(qy);
      const base = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Pour chaque session, calculer le total de ventes associé
      const withSales = await Promise.all(
        base.map(async (s) => {
          const salesQ = query(
            collection(db, "sales"),
            where("sessionId", "==", s.id)
          );
          const salesSnap = await getDocs(salesQ);

          let totalSales = 0;
          let salesCount = 0;
          salesSnap.forEach((d) => {
            const data = d.data() || {};
            totalSales += Number(data.grandTotal || 0);
            salesCount += 1;
          });

          return {
            ...s,
            _totalSales: totalSales,
            _salesCount: salesCount,
          };
        })
      );

      setSessions(withSales);
      await hydrateUsersFromSessions(withSales);

      if (!withSales.length) {
        setMsg("Aucune session trouvée pour ces filtres.");
      }
    } catch (e) {
      console.error(e);
      setMsg("❌ Erreur de chargement des sessions : " + e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // chargement initial (sans filtre)
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ================== Filtres en mémoire ==================

  const filteredSessions = useMemo(() => {
    let arr = sessions;

    const q = cashierFilter.trim().toLowerCase();
    if (q) {
      arr = arr.filter((s) => {
        const u = userMap[s.openedBy];
        const label = (
          u?.displayName ||
          u?.email ||
          s.openedBy ||
          ""
        ).toLowerCase();
        return label.includes(q);
      });
    }

    return arr;
  }, [sessions, cashierFilter, userMap]);

  // Cumul global
  const globalTotals = useMemo(() => {
    let sumSales = 0;
    let sumCount = 0;
    filteredSessions.forEach((s) => {
      sumSales += Number(s._totalSales || 0);
      sumCount += Number(s._salesCount || 0);
    });
    return { sumSales, sumCount };
  }, [filteredSessions]);

  // ================== Détails d'une session ==================

  async function openSessionDetail(session) {
    setDetailSession(session);
    setDetailSales([]);
    setLoadingDetail(true);
    try {
      const qy = query(
        collection(db, "sales"),
        where("sessionId", "==", session.id),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(qy);
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDetailSales(arr);
    } catch (e) {
      console.error(e);
      setMsg("❌ Erreur chargement ventes : " + e.message);
    } finally {
      setLoadingDetail(false);
    }
  }

  function closeDetail() {
    setDetailSession(null);
    setDetailSales([]);
  }

  // ================== UI ==================

  return (
    <div className="container-xxl py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <h4 className="m-0">Rapport de caisse (Admin)</h4>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={loadSessions}
          disabled={loading}
        >
          {loading ? "Chargement…" : "Rafraîchir"}
        </button>
      </div>

      {/* Filtres */}
      <div className="card mb-3">
        <div className="card-body row g-2 align-items-end">
          <div className="col-12 col-md-3">
            <label className="form-label small">Lieu</label>
            <input
              className="form-control"
              placeholder="ex: boutique"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small">Du</label>
            <input
              type="date"
              className="form-control"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small">Au</label>
            <input
              type="date"
              className="form-control"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small">Caissier (nom / email)</label>
            <input
              className="form-control"
              placeholder="Filtrer par nom..."
              value={cashierFilter}
              onChange={(e) => setCashierFilter(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-2 text-md-end">
            <button
              type="button"
              className="btn btn-dark w-100"
              onClick={loadSessions}
              disabled={loading}
            >
              {loading ? "Recherche…" : "Appliquer filtres"}
            </button>
          </div>
        </div>
      </div>

      {/* Cumul global */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="small text-muted">Nombre de sessions</div>
              <div className="h5 m-0">{filteredSessions.length}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="small text-muted">Nombre total de ventes</div>
              <div className="h5 m-0">{globalTotals.sumCount}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="small text-muted">Montant total des ventes</div>
              <div className="h5 m-0">{fmtAmount(globalTotals.sumSales)}</div>
            </div>
          </div>
        </div>
      </div>

      {msg && <div className="alert alert-info py-2">{msg}</div>}

      {/* Tableau sessions + panneau détails */}
      <div className="row g-3">
        {/* Liste des sessions */}
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="mb-3">Sessions de caisse</h6>
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Session</th>
                      <th>Caissier</th>
                      {/* <th>Lieu</th> */}
                      <th>Ouverture</th>
                      <th>Fermeture</th>
                      <th className="text-end">Ventes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.map((s) => (
                      <tr
                        key={s.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => openSessionDetail(s)}
                      >
                        <td className="small">
                          {/* <div>
                            <code>{s.id}</code>
                          </div> */}
                          <span
                            className={
                              "badge mt-1 " +
                              ((s.status || "").toLowerCase() === "closed"
                                ? "bg-success"
                                : "bg-warning text-dark")
                            }
                          >
                            {s.status || "open"}
                          </span>
                        </td>
                        <td className="small">
                          <div>Ouverte par :</div>
                          <div className="fw-semibold">
                            {displayUser(s.openedBy)}
                          </div>
                          {s.closedBy && (
                            <div className="text-muted">
                              Fermée par : {displayUser(s.closedBy)}
                            </div>
                          )}
                        </td>
                        {/* <td className="small">
                          {s.locationId || <span className="text-muted">—</span>}
                        </td> */}
                        <td className="small">{fmtDate(s.openedAt)}</td>
                        <td className="small">{fmtDate(s.closedAt)}</td>
                        <td className="text-end">
                          <div className="fw-semibold">
                            {fmtAmount(s._totalSales || 0)}
                          </div>
                          <div className="small text-muted">
                            {s._salesCount || 0} vente(s)
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!filteredSessions.length && !loading && (
                      <tr>
                        <td
                          colSpan={6}
                          className="text-center text-muted py-3"
                        >
                          Aucune session pour ces filtres.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {loading && (
                <div className="text-center text-muted small">
                  Chargement…
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Détail d'une session : liste des ventes */}
        <div className="col-12 col-lg-5">
          <div className="card shadow-sm h-100">
            <div className="card-body d-flex flex-column">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="m-0">Détails session</h6>
                {detailSession && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={closeDetail}
                  >
                    Fermer
                  </button>
                )}
              </div>

              {!detailSession ? (
                <div className="text-muted small">
                  Clique sur une session dans le tableau pour voir la liste des
                  ventes.
                </div>
              ) : (
                <>
                  <div className="small mb-2">
                    <div>
                      <span className="text-muted">Session :</span>{" "}
                      <code>{detailSession.id}</code>
                    </div>
                    <div>
                      <span className="text-muted">Caissier :</span>{" "}
                      <strong>{displayUser(detailSession.openedBy)}</strong>
                    </div>
                    <div>
                      <span className="text-muted">Lieu :</span>{" "}
                      {detailSession.locationId || "—"}
                    </div>
                    <div>
                      <span className="text-muted">Ouverte :</span>{" "}
                      {fmtDate(detailSession.openedAt)}
                    </div>
                    <div>
                      <span className="text-muted">Fermée :</span>{" "}
                      {fmtDate(detailSession.closedAt)}
                    </div>
                  </div>

                  <hr />

                  <div className="small text-muted mb-2">
                    Ventes de cette session
                  </div>

                  {loadingDetail ? (
                    <div className="text-muted small">Chargement…</div>
                  ) : !detailSales.length ? (
                    <div className="text-muted small">
                      Aucune vente enregistrée sur cette session.
                    </div>
                  ) : (
                    <div
                      style={{ maxHeight: 360, overflowY: "auto" }}
                      className="small"
                    >
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Vente</th>
                            <th>Date</th>
                            <th className="text-end">Montant</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailSales.map((sale) => (
                            <tr key={sale.id}>
                              <td>
                                <code>{sale.id}</code>
                              </td>
                              <td>{fmtDate(sale.createdAt)}</td>
                              <td className="text-end">
                                {fmtAmount(sale.grandTotal || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
